import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { ChevronDown, Image as ImageIcon, MapPin, Tag, Calendar, Clock, Loader2, Timer } from "lucide-react";
import useCountdown from "../../../hooks/use-countdown";

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const TaskCard = ({
  task,
  onAccept = () => { },
  onSendCounter = (taskId, price) => { },
  onExpand = (taskId) => { },
  isLoadingDetails = false,
  isAccepting = false, // New prop for accept loading state
}) => {
  const {
    serviceRequestId,
    customerName,
    customerAvatar,
    customerRating,
    customerRatingCount,
    basePrice,
    jobTitle,
    subcategory,
    serviceType,
    address,
    description,
    images = [],
    video = null,
    distance = null,
    detailsLoaded = false,
    expiryTime = null, // Expiry time from backend
  } = task;

  // Internal state for expiry time to support auto-restart loop
  const [currentExpiryTime, setCurrentExpiryTime] = useState(expiryTime);

  // Sync with prop if it changes (e.g. backend update)
  useEffect(() => {
    setCurrentExpiryTime(expiryTime);
  }, [expiryTime]);

  // Countdown timer
  const { minutes, seconds, isExpired, isWarning } = useCountdown(currentExpiryTime);

  // Auto-restart timer when expired
  useEffect(() => {
    if (isExpired && currentExpiryTime) {
      // Restart timer for 2 minutes from now
      const now = new Date();
      const newExpiry = new Date(now.getTime() + 2 * 60 * 1000).toISOString();
      setCurrentExpiryTime(newExpiry);
    }
  }, [isExpired, currentExpiryTime]);

  // expansions
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [counterOpen, setCounterOpen] = useState(false);

  // counter offer logic
  const [changeSteps, setChangeSteps] = useState(0);
  const [isSendingCounter, setIsSendingCounter] = useState(false);

  const proposedPrice = Math.round(basePrice * (1 + (changeSteps * 0.05)));

  const handleIncrement = () => {
    setChangeSteps((s) => clamp(s + 1, -2, 3));
  };

  const handleDecrement = () => {
    setChangeSteps((s) => clamp(s - 1, -2, 3));
  };

  // Handle expand - fetch details if not loaded
  const handleToggleDetails = () => {
    const newState = !detailsOpen;
    setDetailsOpen(newState);

    // If opening details and not loaded yet, trigger fetch
    if (newState && !detailsLoaded && !isLoadingDetails) {
      onExpand(serviceRequestId);
    }
  };

  // Handle counter offer submission
  const handleSendCounterOffer = async () => {
    setIsSendingCounter(true);
    try {
      await onSendCounter(serviceRequestId, proposedPrice);
      setCounterOpen(false);
      setChangeSteps(0); // Reset counter offer state
    } catch (error) {
      console.error("Failed to send counter offer:", error);
    } finally {
      setIsSendingCounter(false);
    }
  };

  // Handle accept offer
  const handleAcceptOffer = () => {
    onAccept(serviceRequestId);
  };

  // Auto-expand when details are loaded
  useEffect(() => {
    if (detailsLoaded && !detailsOpen) {
      // Optional: auto-expand when details finish loading
      // setDetailsOpen(true);
    }
  }, [detailsLoaded]);

  // Reset counter offer state when modal closes
  useEffect(() => {
    if (!counterOpen) {
      setChangeSteps(0);
      setIsSendingCounter(false);
    }
  }, [counterOpen]);

  return (
    <div className="w-full rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden transform hover:-translate-y-1 relative bg-white border border-gray-100 dark:bg-slate-800 dark:border-slate-700">
      {/* Header Section: Customer Info (Left) & Expiry (Right) */}
      <div className="px-5 py-4 border-b flex justify-between items-center bg-gradient-to-r from-orange-50 to-amber-50 border-orange-100 dark:from-slate-800 dark:to-slate-800 dark:border-slate-700">

        {/* Left: Customer Info */}
        <div className="flex items-center gap-3">
          <div className="flex flex-col items-center">
            <div className="relative">
              <img
                src="https://cdn.pixabay.com/photo/2015/03/04/22/35/avatar-659651_1280.png"
                alt={customerName}
                className="w-12 h-12 rounded-full object-cover border-2 border-white dark:border-slate-600 shadow-sm"
                onError={(e) => {
                  e.target.src = "/static/media/default-avatar.png";
                }}
              />
              <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 bg-green-500 border-white dark:border-slate-800"></div>
            </div>
            <div className="mt-1 text-[10px] font-extrabold uppercase tracking-wider text-[#EFA938] leading-tight">
              {serviceType || "Now"}
            </div>
          </div>
          <div>
            <h3 className="font-bold text-gray-900 dark:text-gray-100 text-sm leading-tight">
              {customerName}
            </h3>
            <div className="flex items-center gap-2">
              <p className="text-xs text-gray-500 dark:text-gray-400">Customer</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                <span className="text-amber-500">★</span> {Number(customerRating ?? 0).toFixed(1)} ({Number(customerRatingCount ?? 0)})
              </p>
            </div>
          </div>
        </div>

        {/* Right: Expiry Timer (if active) */}
        {expiryTime && !isExpired ? (
          <div className={`flex flex-col items-end justify-center px-4 py-1.5 rounded-lg border shadow-sm ${isWarning
            ? "bg-red-50 border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400"
            : "bg-white border-gray-200 text-gray-700 dark:bg-slate-700 dark:border-slate-600 dark:text-gray-300"
            }`}>
            <div className="flex items-center gap-1.5">
              <Timer className={`w-3.5 h-3.5 ${isWarning ? "animate-pulse" : ""}`} />
              <span className="text-sm font-black font-mono tracking-tight leading-none">
                {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
              </span>
            </div>
            <span className="text-[10px] font-bold uppercase tracking-wider opacity-70 mt-0.5">Expires in</span>
          </div>
        ) : expiryTime && isExpired ? (
          <div className="flex flex-col items-center justify-center px-3 py-1.5 rounded-lg bg-gray-100 border border-gray-200 text-gray-500 text-xs font-bold dark:bg-slate-700 dark:border-slate-600 dark:text-gray-400">
            <span className="uppercase tracking-wide text-[10px]">Request</span>
            <span>Expired</span>
          </div>
        ) : null}
      </div>

      {/* Body Section: Service Details, Location, and Price */}
      <div className="p-5 space-y-4">

        {/* Main Service Title & Subcategory */}
        <div className="text-center">
          <h2 className="text-xl font-extrabold text-gray-900 dark:text-white leading-tight">
            {jobTitle}
          </h2>
          {subcategory && (
            <span className="inline-block mt-1 px-3 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-medium dark:bg-blue-900/30 dark:text-blue-300 border border-blue-100 dark:border-blue-800">
              {subcategory}
            </span>
          )}
        </div>

        {/* Middle Details Box */}
        <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 border border-gray-100 dark:border-slate-700 flex flex-col gap-3">

          {/* Location - Horizontal Row */}
          <div className="flex items-center gap-3 w-full">
            <div className="p-2 rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 flex-shrink-0">
              <MapPin className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-bold text-gray-900 dark:text-gray-100 leading-snug">
                {address || "Location not provided"}
              </div>
              {distance && (
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {distance} away
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Price / Offer Section */}
        <div className="flex flex-col items-center justify-center pt-2">
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-widest font-semibold mb-1">
            Customer Offer
          </div>
          <div className="text-3xl font-black text-[#EFA938] tracking-tight">
            <span className="text-lg align-top mr-0.5 text-gray-400 font-bold">PKR</span>
            {basePrice?.toLocaleString()}
          </div>
        </div>

      </div>

      {/* Action Buttons */}
      <div className="px-5 pb-5 flex items-center justify-between gap-3 flex-wrap">
        <button
          onClick={handleToggleDetails}
          disabled={isLoadingDetails}
          className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-[#EFA938] dark:text-gray-300 dark:hover:text-[#EFA938] transition-colors duration-200 disabled:opacity-50"
        >
          {isLoadingDetails ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading...
            </>
          ) : (
            <>
              <ChevronDown
                className={`w-4 h-4 transform transition-transform duration-300 ${detailsOpen ? "rotate-180" : "rotate-0"
                  }`}
              />
              {detailsOpen ? "Hide Details" : "View Details"}
            </>
          )}
        </button>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setCounterOpen(true)}
            disabled={isAccepting}
            className="px-4 py-2.5 rounded-lg border-2 border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 dark:border-slate-600 dark:text-gray-200 dark:hover:bg-slate-700 dark:hover:border-slate-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Counter Offer
          </button>
          <button
            onClick={handleAcceptOffer}
            disabled={isAccepting}
            className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-[#EFA938] to-[#d78c1f] text-sm text-white font-bold hover:from-[#d78c1f] hover:to-[#c57a1a] shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
          >
            {isAccepting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Processing...
              </>
            ) : (
              "Accept Offer"
            )}
          </button>
        </div>
      </div>

      {/* Details Expand */}
      <div
        className={`transition-all duration-500 ease-in-out overflow-hidden ${detailsOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
          }`}
      >
        <div className="px-5 pb-5 pt-3 bg-gray-50 dark:bg-slate-900 border-t border-gray-200 dark:border-slate-700">
          {isLoadingDetails ? (
            <div className="flex items-center justify-center py-8">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-8 h-8 animate-spin text-[#EFA938]" />
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Loading task details...
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <h4 className="font-bold text-sm text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-2">
                  <div className="w-1 h-4 bg-[#EFA938] rounded-full"></div>
                  Job Description
                </h4>
                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed pl-3">
                  {description || "No description provided"}
                </p>
              </div>

              {images && images.length > 0 && (
                <div>
                  <h4 className="font-bold text-sm text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                    <div className="w-1 h-4 bg-[#EFA938] rounded-full"></div>
                    Attached Images
                  </h4>
                  <div className="grid grid-cols-3 gap-3 pl-3">
                    {images.map((img, idx) => (
                      <div
                        key={idx}
                        className="aspect-square bg-gray-100 dark:bg-slate-700 rounded-lg overflow-hidden border-2 border-gray-200 dark:border-slate-600 hover:border-[#EFA938] dark:hover:border-[#EFA938] transition-all duration-200 cursor-pointer group"
                      >
                        <img
                          src={img}
                          alt={`Job image ${idx + 1}`}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          onError={(e) => {
                            e.target.src = "https://via.placeholder.com/300x300?text=No+Image";
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(!images || images.length === 0) && detailsLoaded && (
                <div className="pl-3">
                  <div className="flex items-center justify-center h-32 bg-gray-100 dark:bg-slate-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-slate-600">
                    <div className="text-center text-gray-400 dark:text-gray-500">
                      <ImageIcon className="w-8 h-8 mx-auto mb-2" />
                      <p className="text-sm">No images attached</p>
                    </div>
                  </div>
                </div>
              )}

              {video && (
                <div>
                  <h4 className="font-bold text-sm text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                    <div className="w-1 h-4 bg-[#EFA938] rounded-full"></div>
                    Attached Video
                  </h4>
                  <div className="pl-3">
                    <video
                      controls
                      src={video}
                      className="w-full rounded-lg border-2 border-gray-200 dark:border-slate-600 shadow-sm"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <CounterOfferModal
        isOpen={counterOpen}
        onClose={() => setCounterOpen(false)}
        basePrice={basePrice}
        proposedPrice={proposedPrice}
        changeSteps={changeSteps}
        onIncrement={handleIncrement}
        onDecrement={handleDecrement}
        onSend={handleSendCounterOffer}
        isSending={isSendingCounter}
      />
    </div>
  );
};

export default TaskCard;

const CounterOfferModal = ({
  isOpen,
  onClose,
  basePrice,
  proposedPrice,
  changeSteps,
  onIncrement,
  onDecrement,
  onSend,
  isSending
}) => {
  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn">
      <div className="bg-white dark:bg-slate-900 w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden animate-scaleIn border border-gray-200 dark:border-slate-700">
        <div className="p-5">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <div className="w-1.5 h-6 bg-[#EFA938] rounded-full"></div>
              Propose Counter Offer
            </h3>
            <button
              onClick={onClose}
              disabled={isSending}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ✕
            </button>
          </div>

          {/* Price Adjustment Controls */}
          <div className="bg-white dark:bg-slate-900 rounded-xl p-5 shadow-inner border border-gray-100 dark:border-slate-800 mb-5 bg-gray-50/50">
            <div className="flex items-center justify-between gap-4 mb-5">
              <button
                onClick={onDecrement}
                disabled={changeSteps <= -2 || isSending}
                className="w-12 h-12 rounded-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 hover:border-red-500 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed text-gray-600 dark:text-gray-300 font-bold text-xl shadow-sm transition-all duration-200"
              >
                −
              </button>

              <div className="flex-1 text-center">
                <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                  Base Price
                </div>
                <div className="text-2xl font-bold text-gray-700 dark:text-gray-300">
                  <span className="text-sm text-gray-400 font-medium mr-1">PKR</span>
                  {basePrice.toLocaleString()}
                </div>
              </div>

              <button
                onClick={onIncrement}
                disabled={changeSteps >= 3 || isSending}
                className="w-12 h-12 rounded-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 hover:border-green-500 hover:text-green-500 disabled:opacity-30 disabled:cursor-not-allowed text-gray-600 dark:text-gray-300 font-bold text-xl shadow-sm transition-all duration-200"
              >
                +
              </button>
            </div>

            {/* Price Change Indicator */}
            <div className="flex items-center justify-center gap-2 mb-5 px-2">
              <div className="w-full h-2 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden relative">
                {/* Center Marker */}
                <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gray-400 z-10"></div>

                <div
                  className={`h-full transition-all duration-300 absolute ${changeSteps < 0 ? "bg-red-500 right-1/2 rounded-l-full" : changeSteps > 0 ? "bg-green-500 left-1/2 rounded-r-full" : "bg-transparent"}`}
                  style={{
                    width: `${Math.abs(changeSteps) * 20}%`,
                  }}
                ></div>
              </div>
            </div>

            {/* Proposed Price Display */}
            <div className="bg-gradient-to-r from-[#EFA938] to-[#d78c1f] rounded-xl p-4 text-white text-center shadow-lg transform scale-105">
              <div className="text-xs font-medium text-white/80 uppercase tracking-wider mb-0.5">
                Your New Offer
              </div>
              <div className="text-3xl font-black mb-1 tracking-tight">
                <span className="text-lg opacity-80 font-bold mr-1">PKR</span>
                {proposedPrice.toLocaleString()}
              </div>
              <div className="text-sm font-medium h-5">
                {changeSteps === 0 ? (
                  <span className="opacity-80">--</span>
                ) : (
                  <span className={`inline-flex items-center gap-1 px-2 rounded-full text-xs font-bold ${changeSteps > 0 ? "bg-white/20 text-white" : "bg-red-900/30 text-white"}`}>
                    {changeSteps > 0 ? "▲ Increase" : "▼ Decrease"} {Math.abs(changeSteps * 5)}%
                  </span>
                )}
              </div>
            </div>

            {/* Range indicator */}
            <div className="mt-4 text-[10px] text-center text-gray-400 dark:text-gray-500">
              Adjust price within -10% to +15% range
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              disabled={isSending}
              className="flex-1 px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-700 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800 transition-all duration-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onSend}
              disabled={isSending || changeSteps === 0}
              className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-[#EFA938] to-[#d78c1f] text-sm text-white font-bold hover:from-[#d78c1f] hover:to-[#c57a1a] shadow-lg shadow-orange-500/20 hover:shadow-orange-500/40 transition-all duration-200 transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none flex items-center justify-center gap-2"
            >
              {isSending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                "Send Offer"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};